import { A, useNavigate } from "@solidjs/router";
import { DeleteProjectRequestConfig, useStorytellClient } from "@storytell/client";
import { rankProjects } from "@storytell/core";
import { type Project, type ProjectId, ROLE_OWNER } from "@storytell/types";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbCheck, TbChevronDown, TbCube, TbCubePlus, TbFolders, TbPencil, TbTrash, TbUsers } from "solid-icons/tb";
import { createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { NavRowLabel } from "~/components/navigation/NavRowLabel";
import { roleLabelKey } from "~/components/organization/roleLabels";
import { DeleteProjectModal } from "~/components/projects/DeleteProjectModal";
import { EditProjectModal } from "~/components/projects/EditProjectModal";
import { ShareProjectModal } from "~/components/projects/ShareProjectModal";
import { useGateActionByAuthenticated } from "~/hooks/useGateActionByAuthenticated";
import { useOrgScope } from "~/hooks/useOrgScope";
import { useI18n } from "~/i18n";
import { extractErrorInfo } from "~/lib/error-handling";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

const MENU_ROW =
  "flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-sm text-left text-primary-text " +
  "transition-colors cursor-pointer hover:bg-canvas-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand";

const GROUP_LABEL = "block px-2 pt-1 pb-0.5 text-xs font-semibold uppercase tracking-wide text-secondary-text";

// Restates the switcher's floating surface because this panel is positioned
// here rather than by Kobalte.
const MENU_SURFACE =
  "absolute inset-x-0 top-full z-50 mt-1 max-h-[60vh] overflow-y-auto rounded-lg border border-primary-border " +
  "bg-tile px-2 py-1 text-primary-text shadow-xl animate-fade-in-quick motion-reduce:animate-none";

const SELECTOR_TRIGGER =
  "flex w-full min-h-9 items-center gap-1.5 rounded-lg px-4 py-1.5 text-left " +
  "transition-colors cursor-pointer hover:bg-canvas active:bg-inset " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand";

const MAX_RECENT_PROJECTS_IN_NAV = 5;

/** Project row and its panel; falls back to the switcher modal when no project is active. */
export const ProjectsNavSectionExpanded = (props: { onOpenSwitcher: () => void; onCreateProject: () => void }) => {
  const wire = useWire();
  const nav = wire.navigation;
  const layout = wire.ui.layout;
  const navigate = useNavigate();
  const client = useStorytellClient();
  const { t } = useI18n();
  const scope = useOrgScope();

  // On scope-less routes (e.g. /upgrade) the URL and /context carry no project,
  // so fall back to the last project the user was in to keep the nav anchored.
  const activeProjectId = () => wire.currentProjectId() ?? wire.userContext.getRecentProjectIds()[0];

  const activeProject = createMemo(() => {
    const id = activeProjectId();
    if (!id) return undefined;
    return wire.projects.get(id as ProjectId);
  });

  const activeProjectName = () => activeProject()?.name;

  // Nearly every project would report ownership, so only the roles that don't
  // own earn the width. A role reached through the organization is the one the
  // row above already names, so it stands alone; only a role held on the project
  // alone says so.
  const accessLabel = (project: Project) => {
    if (!project.role || project.role === ROLE_OWNER) return undefined;
    const role = t(roleLabelKey(project.role));
    return project.roleSource === "project" ? t("project.access_direct", { role }) : role;
  };

  // A project outside the focused organization names its own, since otherwise it
  // reads as belonging to the organization the row above names.
  const rowDetail = (project: Project) => {
    const focusedOrgId = scope.currentOrganization()?.organizationId;
    const owningOrg =
      focusedOrgId && project.organizationId !== focusedOrgId
        ? scope.organizationNameById(project.organizationId)
        : undefined;
    return [owningOrg, accessLabel(project)].filter(Boolean).join(" · ");
  };

  const isOnProjectHome = createMemo(() => nav.state().primaryId === undefined);

  // Plain function, not createMemo: a memo created mid-router-transition reads
  // back as undefined until the transition commits, and <A>/navigate() crash on
  // an undefined href during resolvePath. A function evaluates inline and always
  // yields a string. See ENG-6325 (and the same class of bug in ENG-6233).
  const homeHref = () => {
    const id = activeProjectId();
    if (id) return `/project/${id}`;
    const orgId = wire.homeOrgId();
    return orgId ? `/orgs/${orgId}` : "/";
  };

  const orderedRecentProjects = createMemo(() => {
    const activeId = activeProjectId();
    const ranked = rankProjects(scope.reachableProjects(), "", wire.userContext.getRecentProjectIds());
    return ranked.filter((p) => p.id !== activeId);
  });

  const displayedRecentProjects = createMemo(() => orderedRecentProjects().slice(0, MAX_RECENT_PROJECTS_IN_NAV));

  // Recency picks which projects appear; the organization decides where they
  // sit. A project from elsewhere listed above one from the focused organization
  // reads as if the scope had moved out from under the user. Plain function, not
  // a memo: each call builds fresh arrays a memo could not short-circuit on.
  const groupedRecentProjects = () => {
    const focusedOrgId = scope.currentOrganization()?.organizationId;
    const recents = displayedRecentProjects();
    if (!focusedOrgId) return { focused: recents, other: [] as Project[] };
    return {
      focused: recents.filter((project) => project.organizationId === focusedOrgId),
      other: recents.filter((project) => project.organizationId !== focusedOrgId),
    };
  };

  const handleNavigateHome = (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    setIsOpen(false);
    nav.navigateHome();
    if (layout.isSmallScreen()) layout.setNavbarCollapsed(true);
  };

  const [isOpen, setIsOpen] = createSignal(false);
  const [shareModalOpen, setShareModalOpen] = createSignal(false);
  const [editModalOpen, setEditModalOpen] = createSignal(false);
  const [deleteModalOpen, setDeleteModalOpen] = createSignal(false);
  const [deleteError, setDeleteError] = createSignal<string | null>(null);
  let containerRef: HTMLDivElement | undefined;

  const openProject = () => (isOpen() ? activeProject() : undefined);

  // A user who reaches a single project has nowhere to switch to, so the panel
  // keeps only creating a project and acting on this one. Counted across
  // organizations, since the list below is cross-org too.
  const hasProjectsToSwitchTo = () => scope.reachableProjects().length > 1;

  const handleShareClick = useGateActionByAuthenticated(
    "shareProject",
    () => ({
      title: t("modal_signup_manage_project_title"),
      description: t("modal_signup_manage_project_desc"),
      actionType: "share",
    }),
    () => setShareModalOpen(true)
  );

  const handleEditClick = useGateActionByAuthenticated(
    "manageProject",
    () => ({
      title: t("modal_signup_manage_project_title"),
      description: t("modal_signup_manage_project_desc"),
    }),
    () => setEditModalOpen(true)
  );

  const handleDeleteProject = async () => {
    const pid = activeProjectId();
    if (!pid) return;
    setDeleteError(null);

    try {
      const request = DeleteProjectRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(request, { id: pid }, undefined);

      if (response.isErr()) {
        setDeleteError(extractErrorInfo(response.error).message ?? t("project_delete_failed"));
        return;
      }
      trackFeature({ feature: "project", action: "delete", via: "menu" });

      wire.projects?.remove?.(pid as ProjectId);

      const otherProject = (wire.projects?.all?.() ?? []).find((p) => p.id !== pid);
      if (otherProject) {
        wire.userContext.switchToProject(otherProject.id);
      }

      await wire.projects?.refetch?.();

      setTimeout(() => {
        navigate("/");
      }, 250);
    } catch (err) {
      setDeleteError(extractErrorInfo(err).message ?? t("project_delete_error"));
    } finally {
      setDeleteModalOpen(false);
    }
  };

  // Opening requires a deliberate click so a stray hover near the adjacent
  // New Chat button can never surface Create Project (ENG: misclick fix).
  const toggleOpen = () => {
    if (!activeProject()) {
      props.onOpenSwitcher();
      return;
    }
    setDeleteError(null);
    setIsOpen((open) => !open);
  };

  const handleFocusOut = (e: FocusEvent) => {
    const currentTarget = e.currentTarget as HTMLElement | null;
    const nextFocus = e.relatedTarget as Node | null;
    if (currentTarget && nextFocus && currentTarget.contains(nextFocus)) return;
    setIsOpen(false);
  };

  onMount(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (!isOpen()) return;
      const target = e.target as Node | null;
      if (containerRef && target && containerRef.contains(target)) return;
      setIsOpen(false);
    };
    // Escape reaches here after every other consumer, and a picker or the
    // composer that already answered it marks the event handled.
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen() || e.defaultPrevented) return;
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    onCleanup(() => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    });
  });

  const recentProjectRow = (project: Project) => (
    <A
      href={`/project/${project.id}`}
      onClick={(e) => {
        // Let the browser handle cmd/ctrl/shift-click and middle-click
        // natively (opens in new tab/window). Only intercept plain clicks
        // to run the custom switch flow (persist the focused context).
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        setIsOpen(false);
        handleSwitchProject(project);
      }}
      class={MENU_ROW}
    >
      <TbCube class="size-4 shrink-0 text-muted-text" aria-hidden />
      <NavRowLabel name={project.name} detail={rowDetail(project)} />
    </A>
  );

  // Switching projects leaves any in-flight chat generating server-side; it
  // reconnects via playback when the user returns. Don't interrupt it here.
  const handleSwitchProject = (project: Project) => {
    const currentProjectId = wire.activeContext.activeContext()?.projectId;
    if (currentProjectId === project.id) return;

    wire.userContext.switchToProject(project.id);
    if (layout.isSmallScreen()) layout.setNavbarCollapsed(true);
  };

  return (
    <div ref={containerRef} class="relative w-full" onFocusOut={handleFocusOut}>
      <button
        type="button"
        onClick={toggleOpen}
        aria-haspopup="menu"
        aria-expanded={isOpen()}
        aria-controls={openProject() ? "project-menu" : undefined}
        class={twMerge(SELECTOR_TRIGGER, isOpen() && "bg-canvas")}
      >
        <TbCube class="size-4 shrink-0 text-muted-text" aria-hidden />
        <span class="min-w-0 flex-1">
          <span
            class="block truncate text-sm leading-4 font-medium text-primary-text"
            classList={{ "text-muted-text italic": !activeProjectName() }}
          >
            {activeProjectName() ?? t("nav_select_project")}
          </span>
          <Show when={activeProject()}>
            {(project) => (
              <Show when={accessLabel(project())}>
                {(label) => <span class="block truncate text-xs leading-4 text-muted-text">{label()}</span>}
              </Show>
            )}
          </Show>
        </span>
        <TbChevronDown
          class="size-3.5 shrink-0 text-muted-text transition-transform"
          classList={{ "rotate-180": isOpen() }}
          aria-hidden
        />
      </button>

      <Show when={deleteError()}>
        {(message) => (
          <p class="px-4 py-1 text-xs text-red-600 dark:text-red-400" role="alert">
            {message()}
          </p>
        )}
      </Show>

      <Show when={openProject()}>
        {(current) => (
          <div id="project-menu" class={MENU_SURFACE}>
            <Show when={hasProjectsToSwitchTo()}>
              <span class={GROUP_LABEL}>{t("nav_projects")}</span>

              <Show
                when={!isOnProjectHome()}
                fallback={
                  <div class={twMerge(MENU_ROW, "cursor-default hover:bg-transparent")} aria-current="page">
                    <TbCube class="size-4 shrink-0 text-muted-text" aria-hidden />
                    <NavRowLabel name={current().name} detail={rowDetail(current())} current />
                    <TbCheck class="size-4 shrink-0 text-muted-text" aria-hidden />
                  </div>
                }
              >
                <A href={homeHref()} onClick={handleNavigateHome} class={MENU_ROW}>
                  <TbCube class="size-4 shrink-0 text-muted-text" aria-hidden />
                  <NavRowLabel name={current().name} detail={rowDetail(current())} current />
                  <TbCheck class="size-4 shrink-0 text-muted-text" aria-hidden />
                </A>
              </Show>
              <For each={groupedRecentProjects().focused}>{recentProjectRow}</For>
              <Show when={groupedRecentProjects().other.length > 0}>
                <div class="my-1 border-t border-primary-border" aria-hidden />
              </Show>
              <For each={groupedRecentProjects().other}>{recentProjectRow}</For>

              <div class="my-1 border-t border-primary-border" aria-hidden />

              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  props.onOpenSwitcher();
                }}
                class={MENU_ROW}
              >
                <TbFolders class="size-4 shrink-0 text-muted-text" aria-hidden />
                {t("nav_browse_all_projects")}
              </button>

              <div class="my-1 border-t border-primary-border" aria-hidden />
            </Show>

            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                props.onCreateProject();
              }}
              class={twMerge(MENU_ROW, "bg-brand font-medium text-on-brand hover:bg-brand-hover")}
            >
              <TbCubePlus class="size-4 shrink-0" aria-hidden />
              {t("nav_new_project")}
            </button>

            <div class="my-1 border-t border-primary-border" aria-hidden />

            <span class={twMerge(GROUP_LABEL, "truncate")}>{current().name}</span>

            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                handleShareClick();
              }}
              class={MENU_ROW}
            >
              <TbUsers class="size-4 shrink-0 text-muted-text" aria-hidden />
              {t("share_section_title")}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                handleEditClick();
              }}
              class={MENU_ROW}
            >
              <TbPencil class="size-4 shrink-0 text-muted-text" aria-hidden />
              {t("project_menu_edit_name")}
            </button>
            <Show
              when={wire.projects.canDelete(current().id)}
              fallback={
                <StTooltip
                  placement="right"
                  contentProps={{ class: "z-[110]" }}
                  content={t("project_cannot_delete_default_tooltip")}
                >
                  <StTooltipTrigger as="span" class="block">
                    <div
                      class={twMerge(MENU_ROW, "cursor-not-allowed text-muted-text opacity-60 hover:bg-transparent")}
                    >
                      <TbTrash class="size-4 shrink-0" aria-hidden />
                      {t("action.delete_project")}
                    </div>
                  </StTooltipTrigger>
                </StTooltip>
              }
            >
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setDeleteModalOpen(true);
                }}
                class={twMerge(MENU_ROW, "text-red-600 dark:text-red-400")}
              >
                <TbTrash class="size-4 shrink-0" aria-hidden />
                {t("action.delete_project")}
              </button>
            </Show>
          </div>
        )}
      </Show>

      <Show when={activeProject()}>
        {(current) => (
          <>
            <ShareProjectModal
              isOpen={shareModalOpen()}
              onClose={() => setShareModalOpen(false)}
              projectId={current().id}
            />
            <EditProjectModal
              isOpen={editModalOpen()}
              onClose={() => setEditModalOpen(false)}
              projectId={current().id}
            />
            <DeleteProjectModal
              isOpen={deleteModalOpen()}
              onClose={() => setDeleteModalOpen(false)}
              onConfirm={() => void handleDeleteProject()}
              projectName={current().name}
            />
          </>
        )}
      </Show>
    </div>
  );
};
